// src/components/Notifications.js - VERSION CORRIGÉE ET OPTIMISÉE

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSocket } from '../SocketContext';
import { useTranslation } from 'react-i18next';
import { 
    Container, Card, ListGroup, Button, Alert, Badge, 
    Spinner, Modal, Toast, ToastContainer 
} from 'react-bootstrap';
import { 
    FaBell, FaGift, FaRocket, FaCheckCircle, FaTimesCircle, 
    FaTrashAlt, FaChevronRight, FaExclamationTriangle, 
    FaCheck, FaRegBell 
} from 'react-icons/fa';
import './Notifications.css';

// --- Fonctions utilitaires améliorées ---

const getNotificationStyle = (notif) => {
    if (!notif) return { icon: <FaRegBell />, color: 'text-secondary', variant: 'info' };
    
    const type = notif.type || '';
    const message = notif.message || '';

    if (type.includes('messageReply')) return { icon: <FaBell />, color: 'text-primary', variant: 'primary' };
    if (type.includes('withdrawal_approved') || message.includes('gain double')) 
        return { icon: <FaCheckCircle />, color: 'text-success', variant: 'success' };
    if (type.includes('withdrawal_rejected')) 
        return { icon: <FaTimesCircle />, color: 'text-danger', variant: 'danger' };
    if (message.includes('Félicitations') || message.includes('succès')) 
        return { icon: <FaGift />, color: 'text-info', variant: 'info' };
    if (message.includes('créé avec succès') || message.includes('actif')) 
        return { icon: <FaRocket />, color: 'text-info', variant: 'info' };
    if (message.includes('Erreur') || message.includes('problème')) 
        return { icon: <FaExclamationTriangle />, color: 'text-warning', variant: 'warning' };

    return { icon: <FaRegBell />, color: 'text-secondary', variant: 'info' };
};

const formatTimeAgo = (dateString) => {
    if (!dateString) return "Date inconnue";
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Date invalide";
        
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        // Moins d'une minute
        if (diffInSeconds < 60) return "À l'instant";
        
        // Moins d'une heure
        if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} min${minutes > 1 ? 's' : ''}`;
        }
        
        // Moins d'un jour
        if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} h`;
        }
        
        // Moins d'une semaine
        if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} jour${days > 1 ? 's' : ''}`;
        }
        
        // Moins d'un mois
        if (diffInSeconds < 2592000) {
            const weeks = Math.floor(diffInSeconds / 604800);
            return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
        }
        
        // Format complet pour les dates anciennes
        return date.toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'short',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    } catch (error) {
        console.error('Erreur de formatage de date:', error);
        return "Date invalide";
    }
};

// --- Composant principal Notifications ---

const Notifications = () => {
    const { t } = useTranslation();
    const socketContext = useSocket();
    
    // 🔧 CORRECTION 1: Validation du contexte socket
    const {
        notifications = [],
        unreadCount = 0,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        isLoading = false,
        error = null
    } = socketContext || {};
    
    // 🔧 CORRECTION 2: États locaux pour UX améliorée
    const [deletingId, setDeletingId] = useState(null);
    const [markingAll, setMarkingAll] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    
    // 🔧 CORRECTION 3: Tri optimisé avec useMemo
    const sortedNotifications = useMemo(() => {
        if (!Array.isArray(notifications) || notifications.length === 0) {
            return [];
        }
        
        // Créer un tableau trié sans muter l'original
        return [...notifications].sort((a, b) => {
            // Priorité aux non lus
            const aRead = a.read || false;
            const bRead = b.read || false;
            
            if (!aRead && bRead) return -1;
            if (aRead && !bRead) return 1;
            
            // Ensuite par date (plus récent d'abord)
            try {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA;
            } catch (error) {
                return 0;
            }
        });
    }, [notifications]);
    
    // 🔧 CORRECTION 4: Gestion des actions avec feedback
    const handleNotificationClick = useCallback(async (notifId, isRead) => {
        if (!isRead && typeof markAsRead === 'function') {
            try {
                await markAsRead(notifId);
                // Optionnel: navigation ou autre action
            } catch (error) {
                console.error('Erreur lors du marquage comme lu:', error);
                addToast('Erreur lors du marquage de la notification', 'danger');
            }
        }
    }, [markAsRead]);
    
    const handleMarkAllAsRead = useCallback(async () => {
        if (typeof markAllAsRead !== 'function' || unreadCount === 0) return;
        
        setMarkingAll(true);
        try {
            await markAllAsRead();
            addToast('Toutes les notifications ont été marquées comme lues', 'success');
        } catch (error) {
            console.error('Erreur lors du marquage de toutes les notifications:', error);
            addToast('Erreur lors du marquage des notifications', 'danger');
        } finally {
            setMarkingAll(false);
        }
    }, [markAllAsRead, unreadCount]);
    
    const confirmDelete = useCallback((notifId, notifMessage) => {
        setNotificationToDelete({ id: notifId, message: notifMessage });
        setShowDeleteModal(true);
    }, []);
    
    const handleDelete = useCallback(async () => {
        if (!notificationToDelete || typeof deleteNotification !== 'function') return;
        
        setDeletingId(notificationToDelete.id);
        try {
            await deleteNotification(notificationToDelete.id);
            addToast('Notification supprimée avec succès', 'success');
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            addToast('Erreur lors de la suppression', 'danger');
        } finally {
            setDeletingId(null);
            setShowDeleteModal(false);
            setNotificationToDelete(null);
        }
    }, [notificationToDelete, deleteNotification]);
    
    const addToast = useCallback((message, variant = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, variant }]);
        
        // Auto-dismiss après 5 secondes
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    }, []);
    
    // 🔧 CORRECTION 5: Gestion des erreurs
    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <FaExclamationTriangle className="me-2" />
                    <strong>Erreur de chargement des notifications</strong>
                    <p className="mb-0 mt-2">{error.message || 'Impossible de charger les notifications'}</p>
                </Alert>
            </Container>
        );
    }
    
    if (isLoading && notifications.length === 0) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status" className="text-primary">
                    <span className="visually-hidden">Chargement...</span>
                </Spinner>
                <p className="mt-3">Chargement des notifications...</p>
            </Container>
        );
    }

    return (
        <Container className="py-4 py-md-5 notifications-container">
            {/* Toasts pour le feedback */}
            <ToastContainer position="top-end" className="p-3">
                {toasts.map(toast => (
                    <Toast 
                        key={toast.id}
                        onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        show={true}
                        delay={5000}
                        autohide
                        bg={toast.variant}
                    >
                        <Toast.Header>
                            <FaCheck className="me-2" />
                            <strong className="me-auto">Notification</strong>
                        </Toast.Header>
                        <Toast.Body>{toast.message}</Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>
            
            {/* En-tête */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h2 mb-2 d-flex align-items-center">
                        <FaBell className="me-3 text-primary" /> 
                        {t('notifications_title', 'Notifications')}
                    </h1>
                    <p className="text-muted mb-0">
                        {sortedNotifications.length} notification{sortedNotifications.length !== 1 ? 's' : ''}
                        {unreadCount > 0 && ` · ${unreadCount} non lue${unreadCount !== 1 ? 's' : ''}`}
                    </p>
                </div>
                
                {unreadCount > 0 && (
                    <Button 
                        variant="primary"
                        onClick={handleMarkAllAsRead}
                        disabled={markingAll || unreadCount === 0}
                        className="d-flex align-items-center"
                    >
                        {markingAll ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Traitement...
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="me-2" />
                                {t('mark_all_read', 'Tout marquer comme lu')}
                                <Badge bg="light" text="dark" className="ms-2">
                                    {unreadCount}
                                </Badge>
                            </>
                        )}
                    </Button>
                )}
            </div>
            
            {/* Contenu principal */}
            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    {sortedNotifications.length === 0 ? (
                        <Alert variant="light" className="m-0 text-center border-0">
                            <div className="py-5">
                                <FaRegBell className="mb-3 text-muted" size={48} />
                                <h5 className="fw-bold text-muted">
                                    {t('no_notifications_title', 'Aucune notification')}
                                </h5>
                                <p className="text-muted mb-0">
                                    {t('no_notifications_text', 'Vous n\'avez aucune notification pour le moment.')}
                                </p>
                            </div>
                        </Alert>
                    ) : (
                        <ListGroup variant="flush" className="notifications-list">
                            {sortedNotifications.map((notif) => {
                                const { icon, color, variant } = getNotificationStyle(notif);
                                const isUnread = !notif.read;
                                const isDeleting = deletingId === notif._id;
                                
                                return (
                                    <ListGroup.Item 
                                        key={notif._id || `notif-${notif.createdAt}-${notif.message}`}
                                        action
                                        onClick={() => handleNotificationClick(notif._id, notif.read)}
                                        className={`notification-item ${isUnread ? 'notification-unread' : ''} ${isDeleting ? 'opacity-50' : ''}`}
                                        disabled={isDeleting}
                                    >
                                        <div className="d-flex align-items-start w-100">
                                            {/* Icône */}
                                            <div className={`notification-icon ${color} me-3`}>
                                                {icon}
                                            </div>
                                            
                                            {/* Contenu */}
                                            <div className="flex-grow-1">
                                                <div className={`notification-message ${isUnread ? 'fw-bold' : 'text-muted'}`}>
                                                    {notif.message || 'Notification sans message'}
                                                </div>
                                                
                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <small className="notification-time text-muted">
                                                        {formatTimeAgo(notif.createdAt)}
                                                    </small>
                                                    
                                                    {notif.type && (
                                                        <Badge 
                                                            bg={variant}
                                                            className="notification-type"
                                                        >
                                                            {notif.type.replace('_', ' ')}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Actions */}
                                            <div className="notification-actions ms-3">
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    aria-label="Supprimer la notification"
                                                    title="Supprimer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirmDelete(notif._id, notif.message);
                                                    }}
                                                    disabled={isDeleting}
                                                    className="notification-delete-btn"
                                                >
                                                    {isDeleting ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <FaTrashAlt />
                                                    )}
                                                </Button>
                                                
                                                <FaChevronRight className="ms-3 text-muted" />
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>
            
            {/* Modal de confirmation de suppression */}
            <Modal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setNotificationToDelete(null);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaExclamationTriangle className="me-2 text-warning" />
                        Confirmer la suppression
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer cette notification ?</p>
                    {notificationToDelete?.message && (
                        <Alert variant="light" className="small">
                            "{notificationToDelete.message.substring(0, 100)}..."
                        </Alert>
                    )}
                    <p className="text-muted small">
                        Cette action est irréversible.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => {
                            setShowDeleteModal(false);
                            setNotificationToDelete(null);
                        }}
                    >
                        Annuler
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={deletingId === notificationToDelete?.id}
                    >
                        {deletingId === notificationToDelete?.id ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Notifications;